import { createMultiStyleConfigHelpers, defineStyle, } from '@chakra-ui/react';
import { checkboxAnatomy as parts } from '@chakra-ui/anatomy';
import { ThemeColorVariable } from '../../theme-types';
import { ThemeBorderToken, ThemeColorToken, ThemeFontSizeToken, ThemeFontToken, } from '../theme-tokens';
const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(parts.keys);
const baseLabelStyle = defineStyle({
    m: 0,
    fontFamily: ThemeFontToken.Body,
    fontSize: ThemeFontSizeToken.Medium,
    color: ThemeColorToken.OnSurface,
    width: '100%',
    _disabled: {
        opacity: 1,
    },
});
const onHoverSupport = '@media(hover: hover)';
const disabledControlLabelHoverStyleSelector = '.chakra-checkbox__control[data-disabled] + .chakra-checkbox__label:hover';
const uncheckedControlStyleSelector = '.chakra-checkbox__control:not([data-disabled])';
const checkedControlStyleSelector = '.chakra-checkbox__control:not([data-disabled])[data-checked]';
const indeterminateControlStyleSelector = '.chakra-checkbox__control:not([data-disabled])[data-indeterminate]';
const baseContainerStyle = defineStyle({
    [onHoverSupport]: {
        [disabledControlLabelHoverStyleSelector]: {
            cursor: 'not-allowed',
        },
        _hover: {
            [uncheckedControlStyleSelector]: {
                borderColor: ThemeColorVariable.AccentHover,
                color: ThemeColorVariable.AccentHover,
            },
            [checkedControlStyleSelector]: {
                bg: ThemeColorVariable.AccentHover,
                color: ThemeColorVariable.OnAccent,
            },
            [indeterminateControlStyleSelector]: {
                borderColor: ThemeColorVariable.AccentHover,
                bg: ThemeColorVariable.AccentHover,
                color: ThemeColorVariable.OnAccent,
            },
        },
    },
    _active: {
        [uncheckedControlStyleSelector]: {
            borderColor: ThemeColorVariable.AccentActive,
            color: ThemeColorVariable.AccentActive,
        },
        [checkedControlStyleSelector]: {
            bg: ThemeColorVariable.AccentActive,
            borderColor: ThemeColorVariable.AccentActive,
            color: ThemeColorVariable.OnAccent,
        },
        [indeterminateControlStyleSelector]: {
            color: ThemeColorVariable.OnAccent,
            bg: ThemeColorVariable.AccentActive,
            borderColor: ThemeColorVariable.AccentActive,
        },
    },
});
// Helper variables for more complex css selectors
const checkedHoverStyleSelector = '[data-checked]:not([data-disabled])&:hover';
const uncheckedHoverStyleSelector = ':not([data-checked]):not([data-disabled]):not([data-invalid]):not([data-indeterminate])&:hover';
const indeterminateHoverStyleSelector = '[data-indeterminate]:not([data-invalid]):not([data-disabled]):hover';
const invalidHoverStyleSelector = '[data-invalid]:not([data-checked]):hover';
const activeStyleSelector = '[data-active]:not([data-disabled])';
const baseControlStyle = defineStyle({
    fontWeight: 900,
    color: ThemeColorToken.OnBackground,
    borderColor: ThemeColorToken.OnBackground,
    background: 'transparent',
    borderRadius: '4px',
    border: ThemeBorderToken.Small,
    _disabled: {
        opacity: 0.48,
        borderColor: ThemeColorToken.OnBackground,
        background: 'transparent',
    },
    _checked: {
        background: ThemeColorToken.Accent,
        borderColor: ThemeColorToken.Accent,
        _disabled: {
            background: ThemeColorToken.Accent,
            borderColor: ThemeColorToken.Accent,
            color: ThemeColorToken.OnAccent,
        },
    },
    _invalid: {
        borderColor: ThemeColorToken.Error,
        boxShadow: `0 0 0 4px ${ThemeColorVariable.ErrorContainer}`,
    },
    _indeterminate: {
        background: ThemeColorToken.Accent,
        borderColor: ThemeColorToken.Accent,
        color: ThemeColorToken.OnAccent,
        _disabled: {
            borderColor: ThemeColorToken.Accent,
            background: ThemeColorToken.Accent,
        },
    },
    [onHoverSupport]: {
        _hover: {
            cursor: 'pointer',
        },
        '[data-disabled]&:hover': {
            cursor: 'not-allowed',
        },
        [checkedHoverStyleSelector]: {
            bg: ThemeColorVariable.AccentHover,
            borderColor: ThemeColorVariable.AccentHover,
            color: ThemeColorVariable.OnAccent,
            _active: {
                bg: ThemeColorVariable.AccentActive,
                borderColor: ThemeColorVariable.AccentActive,
                color: ThemeColorVariable.OnAccent,
            },
        },
        [uncheckedHoverStyleSelector]: {
            borderColor: ThemeColorVariable.AccentHover,
            color: ThemeColorVariable.AccentHover,
            _active: {
                borderColor: ThemeColorVariable.AccentActive,
                color: ThemeColorVariable.AccentActive,
            },
        },
        [indeterminateHoverStyleSelector]: {
            bg: ThemeColorVariable.AccentHover,
            borderColor: ThemeColorVariable.AccentHover,
            color: ThemeColorVariable.OnAccent,
        },
        [invalidHoverStyleSelector]: {
            color: ThemeColorVariable.ErrorHover,
            borderColor: ThemeColorVariable.ErrorHover,
        },
    },
    [activeStyleSelector]: {
        borderColor: ThemeColorVariable.AccentActive,
        color: ThemeColorVariable.AccentActive,
        _checked: {
            background: ThemeColorVariable.AccentActive,
            borderColor: ThemeColorVariable.AccentActive,
            color: ThemeColorVariable.OnAccent,
        },
        _indeterminate: {
            color: ThemeColorVariable.OnAccent,
            bg: ThemeColorVariable.AccentActive,
        },
    },
});
const baseStyle = definePartsStyle({
    control: baseControlStyle,
    label: baseLabelStyle,
    container: baseContainerStyle,
});
const sizes = {
    sm: definePartsStyle({
        control: {
            width: '20px',
            height: '20px',
        },
    }),
    md: definePartsStyle({
        control: {
            width: '24px',
            height: '24px',
        },
    }),
    lg: definePartsStyle({
        control: {
            width: '40px',
            height: '40px',
            borderRadius: 'full',
        },
    }),
};
const addonVariant = definePartsStyle({
    control: {
        _invalid: {
            color: ThemeColorToken.Error,
        },
        _checked: {
            color: ThemeColorToken.OnAccent,
            borderColor: ThemeColorToken.Accent,
            background: ThemeColorToken.Accent,
            _invalid: {
                color: ThemeColorToken.OnAccent,
            },
        },
    },
});
const normalVariant = definePartsStyle({
    control: {
        _checked: {
            bg: ThemeColorToken.Accent,
            color: ThemeColorToken.OnAccent,
        },
    },
});
const variants = {
    normal: normalVariant,
    addon: addonVariant,
};
export const checkboxConfig = defineMultiStyleConfig({
    baseStyle,
    sizes,
    variants,
});
//# sourceMappingURL=checkbox-theme.js.map